import { Component, Inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { Store, select, State } from '@ngrx/store';

import { AppState, AuthActionTypes, selectProgram } from './core/ngrx';
import { AuthService } from './auth/auth.service';
import { VWProgram } from '@app/core/models';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus, SilentRequest } from '@azure/msal-browser';
import { MSALAuthService } from './auth/msal-auth.service';
import { environment } from '@env/environment';
import { IdTokenClaims } from '@azure/msal-common';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string,
  tfp?: string,
};
@Component({
  selector: 'ecp-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  // @select(['auth', 'error'])
  authError$: Observable<any>;
  /**
   * loading$
   */
  // @select(['user', 'pending'])
  loading$: Observable<boolean>;
  // @select(['user', 'profile', 'program'])
  public program$: Observable<VWProgram>;
  // @select(['user', 'error'])
  error$: Observable<any>;
  theme = 'one-portal';
  tenant: string | null;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private authService: MSALAuthService,
    private store: Store<AppState>,
    private _snackBar: MatSnackBar,
    private state: State<AppState>,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalAuthService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {
    this.error$ = this.store.pipe(select(state$ => state$.user.error));
    this.error$.subscribe(err => {
      if (err) {
        // if (err.name === 'TimeoutError') {
        //   this.openSnackBar(
        //     'There was a problem loading the user profile. Please try again later.',
        //     'Close',
        //   );
        // }
      }
    });
  }

  ngOnInit(): void {
    this.msalAuthService.initialize().subscribe();
    this.msalAuthService.handleRedirectObservable().subscribe();
    this.checkLogin();
    this.msalAuthService.instance.enableAccountStorageEvents();
    this.handleMSALAuth();
    this.authError$ = this.store.pipe(select(state => state.auth.error));
    this.loading$ = this.store.pipe(select(state => state.user.pending));
    this.program$ = this.store.pipe(select(selectProgram));
    this.error$ = this.store.pipe(select(state => state.user.error));
    this.store.pipe(select(state => state.user)).subscribe(user => {
      const tenant = user?.profile?.program?.programKey;
      this.tenant = tenant;
    });
    this.program$.pipe().subscribe(program => {
      if (this.state.getValue().user.patientID !== '') {
        this.theme = 'VWFD';
        return;
      }
      if (program) {
        this.theme = program.programKey;
      }
    });
    this.listenToNavigationEnd().subscribe(data => {
      window.scrollTo(0, 0);
      this.updatePageTitle(data['title']);
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }


  private openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action);
    setTimeout(() => {
      this.router.navigateByUrl('/auth/logout');
    }, 50);
  }

  private updatePageTitle(title: string): void {
    if (!title) return this.titleService.setTitle(`MyFrameBoard® Portal`);
    this.titleService.setTitle(`${title} | MyFrameBoard® Portal`);
  }

  private listenToNavigationEnd(): Observable<any> {
    return this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route: ActivatedRoute) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter(route => route.outlet === 'primary'),
      mergeMap(route => route.data),
    );
  }

  private handleMSALAuth(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
      )
      .subscribe((result: EventMessage) => {
        if (this.msalAuthService.instance.getAllAccounts().length === 0) {
          window.location.pathname = "/";
        } else {
          this.checkLogin();
        }
      });
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        this.checkLogin();
        this.checkAndSetActiveAccount();
      })

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
          || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
          || msg.eventType === EventType.SSO_SILENT_SUCCESS),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((result: EventMessage) => {
        console.log(result, 'result');
        const payload = result.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

        if (idtoken.acr === "B2C_1A_SIGNIN_FRAMEDREAMMULTITENANCY" || idtoken.tfp === "B2C_1A_SIGNIN_FRAMEDREAMMULTITENANCY") {
          this.msalAuthService.instance.setActiveAccount(payload.account);
        }
      });
  }

  private checkLogin(): void {
    const accounts = this.msalAuthService.instance.getAllAccounts();

    if (accounts.length > 0) {
      const accessTokenRequest = {
        scopes: environment.MSAL.scopes,
        account: accounts[0]
      };
      this.store.dispatch({ type: AuthActionTypes.LOGIN_SUCCESS });
      this.msalAuthService.acquireTokenSilent(accessTokenRequest).subscribe((res) => {
        this.authService.setAccessToken(res.accessToken);
        console.log(this.authService.getAccessToken(), 'access token');
      })
    }
  }

  private checkAndSetActiveAccount() {

    const activeAccount = this.msalAuthService.instance.getActiveAccount();

    if (!activeAccount && this.msalAuthService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalAuthService.instance.getAllAccounts();
      this.msalAuthService.instance.setActiveAccount(accounts[0]);
    }
  }
}
